import React from 'react';
import {
  DollarSign,
  TrendingUp,
  CreditCard,
  AlertTriangle,
  Package,
  FileText,
  GitBranch,
  Calculator,
  Tag,
  ChevronRight,
  BookOpen,
  HelpCircle,
  ChevronLeft,
} from 'lucide-react';
import { ModuleId } from '../types';

interface SidebarProps {
  currentModule: ModuleId | null;
  onModuleSelect: (moduleId: ModuleId) => void;
  collapsed?: boolean;
  onToggle?: () => void;
}

const modules = [
  { id: 'cashflow' as ModuleId, name: 'Cash Flow', subtitle: 'Optimization', icon: DollarSign, gradient: 'from-success-500 to-success-600' },
  { id: 'competitive' as ModuleId, name: 'Competitive', subtitle: 'Intelligence', icon: TrendingUp, gradient: 'from-primary-500 to-primary-600' },
  { id: 'debt' as ModuleId, name: 'Debt', subtitle: 'Restructuring', icon: CreditCard, gradient: 'from-accent-500 to-accent-600' },
  { id: 'dynamicPricing' as ModuleId, name: 'Dynamic Pricing', subtitle: 'Revenue Optimization', icon: Tag, gradient: 'from-warning-500 to-warning-600' },
  { id: 'expenses' as ModuleId, name: 'Expense Anomaly', subtitle: 'Detection', icon: AlertTriangle, gradient: 'from-danger-500 to-danger-600' },
  { id: 'inventory' as ModuleId, name: 'Inventory', subtitle: 'Optimization', icon: Package, gradient: 'from-warning-600 to-warning-700' },
  { id: 'payables' as ModuleId, name: 'Payables', subtitle: 'Optimization', icon: FileText, gradient: 'from-primary-600 to-primary-700' },
  { id: 'scenario' as ModuleId, name: 'Scenario', subtitle: 'Planning', icon: GitBranch, gradient: 'from-success-600 to-success-700' },
  { id: 'tax' as ModuleId, name: 'Tax', subtitle: 'Optimization', icon: Calculator, gradient: 'from-accent-600 to-accent-700' },
];

const resources = [
  { id: 'glossary' as ModuleId, name: 'Glossary', subtitle: 'Term Definitions', icon: BookOpen, gradient: 'from-primary-500 to-primary-600' },
  { id: 'faq' as ModuleId, name: 'FAQ', subtitle: 'Help & Support', icon: HelpCircle, gradient: 'from-accent-500 to-accent-600' },
];

const Sidebar: React.FC<SidebarProps> = ({ currentModule, onModuleSelect, collapsed = false, onToggle }) => {
  return (
    <aside className={`${collapsed ? 'w-16' : 'w-72'} bg-gradient-to-b from-white via-gray-50 to-white shadow-lg border-r border-gray-200 fixed left-0 top-[81px] bottom-0 overflow-y-auto overflow-x-visible z-40 transition-all duration-300 ease-in-out`}>
            <div className="p-4">
        <div className={`mb-4 flex items-center ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Optimization Modules</h3>
              <div className="h-1 w-12 bg-gradient-to-r from-primary-500 to-accent-600 rounded-full"></div>
            </div>
          )}
          {onToggle && (
            <button
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onClick={onToggle}
              className="p-2 rounded-lg hover:bg-gray-100 border border-gray-200 text-gray-600 hover:text-primary-600 hover:border-primary-300 transition-all duration-200"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          )}
        </div>

        <nav className="space-y-2 overflow-visible">
          {modules.map((module) => {
            const Icon = module.icon;
            const isActive = currentModule === module.id;

            return (
              <button
                key={module.id}
                onClick={() => onModuleSelect(module.id)}
                aria-label={module.name}
                title={collapsed ? module.name : undefined}
                className={`group relative w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} ${collapsed ? 'px-2' : 'px-4'} py-3.5 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-primary-50 to-primary-100 shadow-lg shadow-primary-100 border-2 border-primary-200'
                    : 'hover:bg-white hover:shadow-md border-2 border-transparent'
                }`}
              >
                <div className={`flex items-center ${collapsed ? '' : 'space-x-3'}`}>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${module.gradient} ${
                    isActive ? 'shadow-lg' : 'opacity-80 group-hover:opacity-100'
                  } transition-all duration-200`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {!collapsed && (
                    <div className="text-left">
                      <div className={`text-sm font-semibold ${
                        isActive ? 'text-primary-900' : 'text-gray-700 group-hover:text-gray-900'
                      }`}>
                        {module.name}
                      </div>
                      <div className={`text-xs ${
                        isActive ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-600'
                      }`}>
                        {module.subtitle}
                      </div>
                    </div>
                  )}
                </div>
                {!collapsed && (
                  <ChevronRight className={`w-4 h-4 transition-all duration-200 ${
                    isActive
                      ? 'text-primary-600 opacity-100'
                      : 'text-gray-400 opacity-0 group-hover:opacity-100'
                  }`} />
                )}
                {collapsed && (
                  <div className="fixed left-16 w-32 h-8 bg-white border border-gray-200 text-gray-800 text-xs flex items-center justify-center z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-md rounded-md">
                    {module.name}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        <div className="mt-8 mb-3">
          {!collapsed && (
            <>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Resources</h3>
              <div className="h-1 w-12 bg-gradient-to-r from-primary-500 to-accent-600 rounded-full"></div>
            </>
          )}
        </div>

        <nav className="space-y-2 overflow-visible">
          {resources.map((resource) => {
            const Icon = resource.icon;
            const isActive = currentModule === resource.id;

            return (
              <button
                key={resource.id}
                onClick={() => onModuleSelect(resource.id)}
                aria-label={resource.name}
                title={collapsed ? resource.name : undefined}
                className={`group relative w-full flex items-center ${collapsed ? 'justify-center' : 'justify-between'} ${collapsed ? 'px-2' : 'px-4'} py-3.5 rounded-xl transition-all duration-200 ${
                  isActive
                    ? 'bg-gradient-to-r from-primary-50 to-primary-100 shadow-lg shadow-primary-100 border-2 border-primary-200'
                    : 'hover:bg-white hover:shadow-md border-2 border-transparent'
                }`}
              >
                <div className={`flex items-center ${collapsed ? '' : 'space-x-3'}`}>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${resource.gradient} ${
                    isActive ? 'shadow-lg' : 'opacity-80 group-hover:opacity-100'
                  } transition-all duration-200`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  {!collapsed && (
                    <div className="text-left">
                      <div className={`text-sm font-semibold ${
                        isActive ? 'text-primary-900' : 'text-gray-700 group-hover:text-gray-900'
                      }`}>
                        {resource.name}
                      </div>
                      <div className={`text-xs ${
                        isActive ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-600'
                      }`}>
                        {resource.subtitle}
                      </div>
                    </div>
                  )}
                </div>
                {!collapsed && (
                  <ChevronRight className={`w-4 h-4 transition-all duration-200 ${
                    isActive
                      ? 'text-primary-600 opacity-100'
                      : 'text-gray-400 opacity-0 group-hover:opacity-100'
                  }`} />
                )}
                {collapsed && (
                  <div className="fixed left-16 w-32 h-8 bg-white border border-gray-200 text-gray-800 text-xs flex items-center justify-center z-[9999] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none shadow-md rounded-md">
                    {resource.name}
                  </div>
                )}
              </button>
            );
          })}
        </nav>

        {!collapsed && (
          <div className="mt-8 p-4 bg-gradient-to-br from-primary-50 to-accent-50 rounded-xl border border-primary-100">
            <div className="flex items-center space-x-2 mb-2">
              <div className="w-2 h-2 bg-success-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-semibold text-gray-700">System Status</span>
            </div>
            <p className="text-xs text-gray-600">All modules operational</p>
          </div>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
